import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { apiService } from '../../services/api';
import MainLayout from '../layout/MainLayout';
import Card, { CardHeader, CardTitle, CardBody } from '../core/Card/Card';
import Badge from '../core/Badge/Badge';
import { LoadingSpinner } from '../common/LoadingSpinner';
import {
  Users, User, Calendar, Target, TrendingUp,
  Video, FileText, ChevronRight,
  Clock, Award, Activity, Star
} from 'lucide-react';
import { formatDate } from '../../utils/formatters';
import '../../styles/mentor-mentees.scss';

interface Mentee {
  id: string;
  mentee_id: string;
  mentee_first_name: string;
  mentee_last_name: string;
  mentee_email: string;
  mentee_current_role: string;
  mentee_company: string;
  start_date: string;
  status: string;
  last_session_date?: string;
  sessions_completed: number;
  goals_achieved: number;
  total_goals: number;
  progress_percentage: number;
  next_session?: {
    date: string;
    topic: string;
  };
}

const MentorMentees: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [mentees, setMentees] = useState<Mentee[]>([]);
  const [selectedMentee, setSelectedMentee] = useState<Mentee | null>(null);
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');

  useEffect(() => {
    loadMentees();
  }, []);

  const loadMentees = async () => {
    try {
      setLoading(true);
      const response = await apiService.mentor.getMentees();
      // Verificar si la respuesta tiene un array data o si es directamente un array
      const menteesData = Array.isArray(response.data) ? response.data : (response.data?.data || []);
      // Con un solo mentee no tiene sentido la lista: se abre su ficha directamente.
      if (menteesData.length === 1 && menteesData[0]?.mentee_id) {
        navigate(`/mentor/mentees/${menteesData[0].mentee_id}`, { replace: true });
        return;
      }
      setMentees(menteesData);
      if (menteesData.length > 0) {
        setSelectedMentee(menteesData[0]);
      }
    } catch (error) {
      console.error('Error loading mentees:', error);
      setMentees([]); // Asegurar que siempre sea un array
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status: string) => {
    const variants: Record<string, 'success' | 'warning' | 'danger' | 'info'> = {
      active: 'success',
      paused: 'warning',
      completed: 'info',
      cancelled: 'danger'
    };
    return <Badge variant={variants[status] || 'default'}>{status}</Badge>;
  };

  const getProgressColor = (percentage: number) => {
    if (percentage >= 75) return '#10B981';
    if (percentage >= 50) return '#F59E0B';
    if (percentage >= 25) return '#101010';
    return '#EF4444';
  };

  if (loading) {
    return (
      <MainLayout>
        <div className="mentor-mentees mentor-mentees--loading">
          <LoadingSpinner size="lg" text="Cargando mentees..." />
        </div>
      </MainLayout>
    );
  }

  return (
    <MainLayout>
      <div className="mentor-mentees">
        {/* Header */}
        <div className="page-header">
          <div className="page-header__info">
            <div className="eyebrow">Mentor · {mentees.length === 1 ? 'Mi Mentee' : 'Mis Mentees'}</div>
            <h1 className="heading-1">{mentees.length === 1 ? 'Mi Mentee' : 'Mis Mentees'}</h1>
            <div className="page-meta">
              {mentees.length} {mentees.length === 1 ? 'mentee activo' : 'mentees activos'} · {mentees.reduce((sum, m) => sum + (Number(m.sessions_completed) || 0), 0)} sesiones completadas
            </div>
          </div>
          <div className="page-header__actions">
            <div className="view-toggle">
              <button
                className={`view-btn ${viewMode === 'grid' ? 'active' : ''}`}
                onClick={() => setViewMode('grid')}
              >
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                  <rect x="1" y="1" width="5" height="5" stroke="currentColor" strokeWidth="1.5"/>
                  <rect x="10" y="1" width="5" height="5" stroke="currentColor" strokeWidth="1.5"/>
                  <rect x="1" y="10" width="5" height="5" stroke="currentColor" strokeWidth="1.5"/>
                  <rect x="10" y="10" width="5" height="5" stroke="currentColor" strokeWidth="1.5"/>
                </svg>
              </button>
              <button
                className={`view-btn ${viewMode === 'list' ? 'active' : ''}`}
                onClick={() => setViewMode('list')}
              >
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                  <rect x="1" y="2" width="14" height="2" fill="currentColor"/>
                  <rect x="1" y="7" width="14" height="2" fill="currentColor"/>
                  <rect x="1" y="12" width="14" height="2" fill="currentColor"/>
                </svg>
              </button>
            </div>
          </div>
        </div>

        {mentees.length === 0 ? (
          <Card>
            <CardBody>
              <div className="empty-state">
                <Users size={64} className="empty-icon" />
                <h3>No tienes mentees asignados</h3>
                <p>Los administradores te asignarán mentees según tu disponibilidad y expertise.</p>
              </div>
            </CardBody>
          </Card>
        ) : (
          <>
            {/* Mentees Grid/List */}
            <div className={`mentees-container mentees-container--${viewMode}`}>
              {viewMode === 'grid' ? (
                <div className="mentees-grid">
                  {mentees.map((mentee) => (
                    <div
                      key={mentee.id}
                      onClick={() => setSelectedMentee(mentee)}
                      style={{ cursor: 'pointer' }}
                    >
                      <Card
                        className={`mentee-card ${selectedMentee?.id === mentee.id ? 'selected' : ''}`}
                        animate
                        animateDelay={0.05 * mentees.indexOf(mentee)}
                      >
                        <CardBody>
                        <div className="mentee-card__header">
                          <div className="mentee-avatar">
                            <span>{mentee.mentee_first_name[0]}{mentee.mentee_last_name[0]}</span>
                          </div>
                          <div className="mentee-info">
                            <h3>{mentee.mentee_first_name} {mentee.mentee_last_name}</h3>
                            <p className="mentee-role">{mentee.mentee_current_role}</p>
                            <p className="mentee-company">{mentee.mentee_company}</p>
                          </div>
                          {getStatusBadge(mentee.status)}
                        </div>

                        <div className="mentee-stats">
                          <div className="stat">
                            <Video size={16} />
                            <span>{mentee.sessions_completed} sesiones</span>
                          </div>
                          <div className="stat">
                            <Target size={16} />
                            <span>{mentee.goals_achieved}/{mentee.total_goals} objetivos</span>
                          </div>
                          <div className="stat">
                            <Calendar size={16} />
                            <span>Desde {formatDate(mentee.start_date)}</span>
                          </div>
                        </div>

                        <div className="mentee-progress">
                          <div className="progress-header">
                            <span>Progreso general</span>
                            <span className="progress-value">{mentee.progress_percentage}%</span>
                          </div>
                          <div className="progress-bar">
                            <div
                              className="progress-fill"
                              style={{
                                width: `${mentee.progress_percentage}%`,
                                backgroundColor: getProgressColor(mentee.progress_percentage)
                              }}
                            />
                          </div>
                        </div>

                        {mentee.next_session && (
                          <div className="next-session">
                            <Clock size={14} />
                            <span>Próxima: {formatDate(mentee.next_session.date)} - {mentee.next_session.topic}</span>
                          </div>
                        )}

                        <div className="mentee-actions">
                          <button
                            className="btn btn--primary btn--small"
                            onClick={(e) => {
                              e.stopPropagation();
                              navigate(`/mentor/mentees/${mentee.mentee_id}`);
                            }}
                          >
                            Ver perfil
                            <ChevronRight size={16} />
                          </button>
                        </div>
                      </CardBody>
                    </Card>
                    </div>
                  ))}
                </div>
              ) : (
                <Card>
                  <CardBody>
                    <div className="mentees-list">
                      <table className="mentees-table">
                        <thead>
                          <tr>
                            <th>Mentee</th>
                            <th>Rol / Empresa</th>
                            <th>Estado</th>
                            <th>Progreso</th>
                            <th>Sesiones</th>
                            <th>Última actividad</th>
                            <th>Acciones</th>
                          </tr>
                        </thead>
                        <tbody>
                          {mentees.map((mentee) => (
                            <tr
                              key={mentee.id}
                              className={selectedMentee?.id === mentee.id ? 'selected' : ''}
                              onClick={() => setSelectedMentee(mentee)}
                            >
                              <td>
                                <div className="mentee-cell">
                                  <div className="mentee-avatar-small">
                                    <span>{mentee.mentee_first_name[0]}{mentee.mentee_last_name[0]}</span>
                                  </div>
                                  <div>
                                    <strong>{mentee.mentee_first_name} {mentee.mentee_last_name}</strong>
                                    <br />
                                    <span className="text-muted">{mentee.mentee_email}</span>
                                  </div>
                                </div>
                              </td>
                              <td>
                                <div>{mentee.mentee_current_role}</div>
                                <div className="text-muted">{mentee.mentee_company}</div>
                              </td>
                              <td>{getStatusBadge(mentee.status)}</td>
                              <td>
                                <div className="progress-cell">
                                  <div className="progress-bar-small">
                                    <div
                                      className="progress-fill"
                                      style={{
                                        width: `${mentee.progress_percentage}%`,
                                        backgroundColor: getProgressColor(mentee.progress_percentage)
                                      }}
                                    />
                                  </div>
                                  <span className="progress-text">{mentee.progress_percentage}%</span>
                                </div>
                              </td>
                              <td>{mentee.sessions_completed}</td>
                              <td>
                                {mentee.last_session_date
                                  ? formatDate(mentee.last_session_date)
                                  : 'Sin actividad'
                                }
                              </td>
                              <td>
                                <div className="table-actions">
                                  <button
                                    className="action-btn"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      navigate(`/mentor/mentees/${mentee.mentee_id}`);
                                    }}
                                  >
                                    <ChevronRight size={18} />
                                  </button>
                                </div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </CardBody>
                </Card>
              )}
            </div>

            {/* Selected Mentee Details */}
            {selectedMentee && viewMode === 'grid' && (
              <Card className="mentee-details" animate animateDelay={0.3}>
                <CardHeader>
                  <CardTitle>
                    Detalles de {selectedMentee.mentee_first_name} {selectedMentee.mentee_last_name}
                  </CardTitle>
                </CardHeader>
                <CardBody>
                  <div className="details-grid">
                    <div className="detail-section">
                      <h4>Información de contacto</h4>
                      <p><strong>Email:</strong> {selectedMentee.mentee_email}</p>
                      <p><strong>Empresa:</strong> {selectedMentee.mentee_company}</p>
                      <p><strong>Rol:</strong> {selectedMentee.mentee_current_role}</p>
                    </div>
                    <div className="detail-section">
                      <h4>Estadísticas de mentoría</h4>
                      <p><strong>Inicio:</strong> {formatDate(selectedMentee.start_date)}</p>
                      <p><strong>Sesiones completadas:</strong> {selectedMentee.sessions_completed}</p>
                      <p><strong>Objetivos logrados:</strong> {selectedMentee.goals_achieved} de {selectedMentee.total_goals}</p>
                    </div>
                  </div>
                  <div className="detail-actions">
                    <button className="btn btn--outline" onClick={() => navigate('/mentor/sessions')}>
                      <Calendar size={18} />
                      Agendar sesión
                    </button>
                    <button className="btn btn--outline" onClick={() => navigate(`/mentor/mentees/${selectedMentee.mentee_id}`)}>
                      <FileText size={18} />
                      Ver plan de desarrollo
                    </button>
                    <button
                      className="btn btn--primary"
                      onClick={() => navigate(`/mentor/mentees/${selectedMentee.mentee_id}`)}
                    >
                      Ver perfil completo
                      <ChevronRight size={18} />
                    </button>
                  </div>
                </CardBody>
              </Card>
            )}
          </>
        )}
      </div>
    </MainLayout>
  );
};

export default MentorMentees;